import { useCallback, useRef, useState } from "react";

/**
 * Play/pause state for the lightbox <video>.
 *
 * `HTMLMediaElement.play()` returns a promise that rejects on iOS / Mobile
 * Safari (e.g. `NotSupportedError`, DOMException code 9, or `NotAllowedError`).
 * If that rejection is left unhandled it surfaces as an `onunhandledrejection`
 * and is captured as a Sentry error (FRONTEND-1Q). We swallow it the same way
 * every other `.play()` call site in the app already does.
 *
 * `isPlaying` is kept in sync with the *real* element state via the returned
 * `onPlay` / `onPause` / `onEnded` handlers, which the component binds to the
 * <video>. On a play() rejection we reset it to `false` so the play overlay
 * stays visible instead of getting stuck hidden.
 */
export function useVideoPlayback() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const togglePlayPause = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().catch(() => setIsPlaying(false));
    } else {
      video.pause();
    }
  }, []);

  const onPlay = useCallback(() => setIsPlaying(true), []);
  const onPause = useCallback(() => setIsPlaying(false), []);
  const onEnded = useCallback(() => setIsPlaying(false), []);

  return { videoRef, isPlaying, togglePlayPause, onPlay, onPause, onEnded };
}
